import { ChevronDown } from "lucide-react";
import { motion } from "motion/react";
import type { JSX } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { libraryCategoryLabel } from "@/lib/library-categories";
import {
  useLibraryCategories,
  useLibraryTemplates,
} from "@/lib/library-queries";
import { cn } from "@/lib/utils";
import { LibraryItem } from "./library-item";

function ReadError({ retry }: { retry: () => Promise<void> }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div role="alert" className="p-6">
      <p>{t("error.unknown")}</p>
      <Button
        onClick={() => {
          void retry();
        }}
      >
        {t("common.retry", "Retry")}
      </Button>
    </div>
  );
}
function LibraryGroup({
  language,
  category,
  label,
}: {
  language: string;
  category: string | null;
  label: string;
}): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { templates, loading, error, refresh } = useLibraryTemplates(
    language,
    category,
    open,
  );
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="border border-grey-200 rounded-lg overflow-hidden">
        <CollapsibleTrigger className="flex w-full items-center justify-between px-6 py-4 bg-grey-50 hover:bg-grey-100 transition-colors">
          <h2 className="text-lg font-semibold text-grey-900">{label}</h2>
          <ChevronDown
            className={cn(
              "h-5 w-5 text-grey-500 transition-transform duration-200",
              open && "transform rotate-180",
            )}
          />
        </CollapsibleTrigger>
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3, ease: [0.645, 0.045, 0.355, 1] }}
          animate={
            open ? { opacity: 1, height: "auto" } : { opacity: 0, height: 0 }
          }
        >
          <CollapsibleContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner size={6} />
              </div>
            ) : error ? (
              <ReadError retry={refresh} />
            ) : templates.length ? (
              <div className="divide-y">
                {templates.map((template) => (
                  <LibraryItem key={template.public_id} template={template} />
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-grey-500">
                {t(
                  "templates.library.emptyCategory",
                  "No templates found in this category",
                )}
              </div>
            )}
          </CollapsibleContent>
        </motion.div>
      </div>
    </Collapsible>
  );
}
export function LibraryList(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { categories, language, loading, error, refresh } =
    useLibraryCategories(i18n.language);
  const label = (category: string | null) =>
    category === null
      ? t("templates.library.noCategory", "Uncategorized (no category)")
      : category === ""
        ? t("templates.library.blankCategory", "Uncategorized (empty)")
        : libraryCategoryLabel(category, t, i18n.language);
  const uncategorized = (category: string | null) =>
    category === null || category === "" || category === "Uncategorized"
      ? 1
      : 0;
  const sorted = [...categories].sort(
    (a, b) =>
      uncategorized(a) - uncategorized(b) || label(a).localeCompare(label(b)),
  );
  if (loading)
    return (
      <div className="flex justify-center mt-20">
        <LoadingSpinner size={8} />
      </div>
    );
  if (error) return <ReadError retry={refresh} />;
  return (
    <div className="space-y-2">
      {sorted.length ? (
        sorted.map((category) => (
          <LibraryGroup
            key={JSON.stringify([i18n.language, language, category])}
            category={category}
            language={language}
            label={label(category)}
          />
        ))
      ) : (
        <p>{t("templates.library.empty", "No templates found")}</p>
      )}
    </div>
  );
}
